// components/LeftPanel.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate, formatDay } from "utils/helpers";
import FileView from "components/fileView";
import { LoadingIcon } from "utils/constant";
import ShowUI, { CanShow, LEGISLATIVE_DRAFTING_UI_RULES } from "utils/authRules";
import { updateLegislativeDrafting } from "services/legislativeService";
import { useToast } from "contexts/ToastContext";
import { SECTOR_DIRECTIVES_REQUEST_STATUS } from "utils/constantValues";
import HeadRemark from "./headRemark";
import DirectorRemark from "./directorRemark";
import CoordinatorRemark from "./coordinatorRemark";
import ProsecutorRemark from "./prosecutorRemark";
import ActionAvatars from "pages/conductLegalResearch/fragments/actionAvatars";
import PublicFeedback from "./publicFeedback";
import UploadAmendedFile from "./uploadAmendedFile";
import DynamicFormInput from "components/form/form";
import { useAuth } from "contexts/AuthContext";
import { StatusChip } from "components/StatusChip";
import RespondBackForm from "pages/conductLegalResearch/fragments/respondBackform";
import LegislativeRespondBackForm from "./LegislativeResponback";
import { updateLegalAdvisory } from "services/legalAdvisoryService";
import { updateLegalTranslation } from "services/legalTranslationService";
import TeamView from "components/team_view";
import DocumentViewerModal from "components/DocumentViewer";
import DistributeToZone from "pages/legalTraningProvision/fragments/distributeToZone";
import UploadDraftLaw from "./uploadDraftLaw";
import BroadcastCard from "pages/conductLegalResearch/fragments/broadcastCard";
import EditTeamModal from "./editTeamModal";
import { useTranslation } from "react-i18next";

function LegalAdvisoryDetail({ request }: { request: any }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [publicFeedback, setPublicFeedback] = useState(false);
    const [uploadAmend, setUploadAmend] = useState(false);
    const [auditDraft, setAuditDraft] = useState(false);
    const [showDraftHistory, setShowDraftHistory] = useState(false);
    const [isDistributeModalOpen, setIsDistributeModalOpen] = useState(false);
    const [edit, setEdit] = useState(false);
    const contextData = useAuth();
    // Helper: check if current user is director
    const isDirector = contextData?.user?.roles?.includes("LRDIFUDDirector");
    const [formState, setFormState] = useState({ ...request });
    // Helper to check if internal type
    const isInternal = formState?.is_internal === true;
    const [viewTeam, setViewTeam] = useState(false);
    const { showToast } = useToast();

    const [isRespondBackFormOpen, setIsRespondBackFormOpen] = useState(false);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [setupTeam, setSetupTeam] = useState<boolean>(formState?.prosecutor_assignments?.length > 0);

    const handleFileChange = (
        file: any,
        fieldName: keyof Pick<
            typeof formState,
            "audit_summary_file"
        >
    ) => {
        setFormState((prev: any) => ({
            ...prev,
            [fieldName]: file,
        }));
    };

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setFormState((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleRespondBackClick = () => {
        setIsRespondBackFormOpen(!isRespondBackFormOpen);
    }

    const handleAdditionalInfoApprove = async () => {
        setLoading(true);
        const data = new FormData();

        data.append("forward_status", SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_registrar.value);

        try {
            const response = await updateLegalTranslation(formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("ProposalApprovedSuccess") || "Legal research proposal Approved!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateApproval") || "Failed to update legal research proposal Approval.", "error");
        } finally {
            setLoading(false);
        }
    }

    const handleReject = () => {
    }

    const handleForward = async (froward_status: string) => {
        const data = new FormData();
        if (formState.status === "Under review" && froward_status === SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_head.value) {
            showToast(t("CannotForwardBeforeEditing") || "Can not forward before editing.", "error");
            return;
        }
        if (formState.status === "Back to Head" && froward_status === SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_director.value) {
            showToast(t("CannotForwardBeforeEditing") || "Can not forward before editing.", "error");
            return;
        }

        if (formState.status === "Back to Director" && froward_status === SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_coordinator.value) {
            showToast(t("CannotForwardBeforeEditing") || "Can not forward before editing.", "error");
            return;
        }

        data.append(
            "forward_status",
            froward_status
        );

        try {
            const response = await updateLegalTranslation(formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("LegislativeDraftForwardedSuccess") || "Legislative Draft Forwarded successfully!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegislativeDraftRequest") || "Failed to update Legislative Draft request.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleAudit = async () => {
        const data = new FormData();
        data.append("audit_summary", formState.audit_summary);
        data.append("audit_summary_file", formState.audit_summary_file);
        data.append("status", SECTOR_DIRECTIVES_REQUEST_STATUS.audit_submitted.value);

        try {
            const response = await updateLegalTranslation(formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("LegislativeDraftForwardedSuccess") || "Legislative Draft Forwarded successfully!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegislativeDraftRequest") || "Failed to update Legislative Draft request.", "error");
        } finally {
            setLoading(false);
            setAuditDraft(false);
        }
    };

    const handleBroadcast = async (isPublished: boolean) => {
        const data = new FormData();
        data.append("is_published", isPublished.toString());

        try {
            const response = await updateLegalTranslation(formState.id, data);

            setFormState((prev: any) => ({ ...prev, is_published: isPublished }));
            showToast(isPublished ? t("LegalTranslationBroadcasted") || "Legal translation broadcasted to public!" : t("LegalTranslationUnpublished") || "Legal translation unpublished!", "success");
        } catch (error) {
            console.error("Error updating broadcast status:", error);
            showToast(t("FailedToUpdateBroadcastStatus") || "Failed to update broadcast status.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleDraftApprove = async () => {
        const data = new FormData();
        data.append("status", SECTOR_DIRECTIVES_REQUEST_STATUS.draft_approved.value);
        data.append("forward_status", SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_registrar.value);

        try {
            const response = await updateLegalTranslation(formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("LegislativeDraftForwardedSuccess") || "Legislative Draft Forwarded successfully!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegislativeDraftRequest") || "Failed to update Legislative Draft request.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleFinalApprove = async () => {
        const data = new FormData();
        data.append("status", SECTOR_DIRECTIVES_REQUEST_STATUS.final_approval.value);

        try {
            const response = await updateLegalTranslation(formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("LegislativeDraftForwardedSuccess") || "Legislative Draft Forwarded successfully!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegislativeDraftRequest") || "Failed to update Legislative Draft request.", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { console.log("formState: ", formState) }, [formState]);

    // Listen for refresh events from EditTeamModal
    useEffect(() => {
        const handleRefreshLegalTranslation = (event: CustomEvent) => {
            if (event.detail) {
                setFormState((prev: any) => ({ ...prev, ...event.detail }));
            }
        };

        window.addEventListener('refreshLegalTranslation', handleRefreshLegalTranslation as EventListener);

        return () => {
            window.removeEventListener('refreshLegalTranslation', handleRefreshLegalTranslation as EventListener);
        };
    }, []);

    return (
        <div className="flex-1 bg-gray-100 mx-4 rounded-lg overflow-y-scroll scrollbar-hide">
            {/* Created By for internal requests */}
            {isInternal && formState?.assigned_by && (
                <div className="flex items-center gap-3 bg-gradient-to-r from-purple-100 to-purple-50 rounded-lg p-4 shadow border border-purple-200 mb-4 max-w-4xl mx-auto mt-6">
                    <div className="flex-shrink-0 bg-purple-500 text-white rounded-full w-11 h-11 flex items-center justify-center font-bold text-lg">
                        {formState.assigned_by.split(' ').map((n: string) => n[0]).join('').slice(0,2)}
                    </div>
                    <div>
                        <div className="text-base font-semibold text-purple-800">{t("CreatedBy") || "Created By"}</div>
                        <div className="text-lg font-medium text-gray-800">{formState.assigned_by}</div>
                    </div>
                </div>
            )}
            <div className="flex flex-col max-w-4xl bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                {/* Status and Forward Status Badge */}
                <div className="flex flex-wrap gap-3 mb-4">
                    <StatusChip status={formState?.status} />
                    {formState?.forward_status && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {t("Forward") || "Forward"}: {formState?.forward_status}
                        </span>
                    )}
                </div>
                
                {/* Header */}
                <div className="flex items-center justify-between pb-4 mb-3">
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => navigate("/legal-translation")}
                            className="text-gray-600 hover:text-gray-800"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 19l-7-7 7-7"
                                />
                            </svg>
                        </button>
                    </div>
                    <h1 className="text-2xl font-semibold text-gray-800 ml-8">
                        {t("LegalTranslationDetails") || "Legal Translation Details"}
                    </h1>
                    
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.stakeholder_forward_to_head.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div onClick={() => navigate("/legal-translation/request", { state: { request: formState } })} className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                                <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                </svg>
                            </div>
                            <div className="flex gap-4">
                                <button onClick={() => handleForward(SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_head.value)} type="button" className="bg-[#212121] hover:bg-[#333333] text-white text-sm font-semibold rounded-lg px-5 py-2.5 transition-colors">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.head_forward_to_director.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <ShowUI
                                ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_head_forward_to_director.value}
                                currentStatus={formState.status}
                                forwardStatus={formState.forward_status}
                            >
                                <div onClick={() => setEdit(!edit)} className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                                    {edit ?
                                        <p>{t("Cancel") || "Cancel"}</p> :
                                        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                        </svg>}
                                </div>
                            </ShowUI>
                            <div className="flex gap-4">
                                <button onClick={() => handleForward(
                                    formState.status === SECTOR_DIRECTIVES_REQUEST_STATUS.under_review.value ? SECTOR_DIRECTIVES_REQUEST_STATUS.not_forwarded.value :
                                        SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_director.value)} type="button" className="bg-[#212121] hover:bg-[#333333] text-white text-sm font-semibold rounded-lg px-5 py-2.5 transition-colors">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.director_forward_to_coordinator.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div onClick={() => setEdit(!edit)} className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                                {edit ?
                                    <p>{t("Cancel") || "Cancel"}</p> :
                                    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                    </svg>}
                            </div>
                            <div className="flex gap-4">
                                <button onClick={() => handleForward(
                                    formState.status === SECTOR_DIRECTIVES_REQUEST_STATUS.back_to_head.value ? SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_head.value :
                                        SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_coordinator.value)} type="button" className="bg-[#212121] hover:bg-[#333333] text-white text-sm font-semibold rounded-lg px-5 py-2.5 transition-colors">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.coordinator_forward_to_head.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div className="flex gap-4">
                                <button onClick={() => handleForward(SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_head.value)}
                                    type="button" className="bg-[#212121] hover:bg-[#333333] text-white text-sm font-semibold rounded-lg px-5 py-2.5 transition-colors">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.additional_info_action.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div className="flex gap-4">
                                <button onClick={() => handleForward(
                                    formState.status === SECTOR_DIRECTIVES_REQUEST_STATUS.additional_info_responded.value ? SECTOR_DIRECTIVES_REQUEST_STATUS.assigned_to_prosecutor.value :
                                        SECTOR_DIRECTIVES_REQUEST_STATUS.not_forwarded.value)} type="button" className="bg-[#212121] hover:bg-[#333333] text-white text-sm font-semibold rounded-lg px-5 py-2.5 transition-colors">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.prosecutor_forward_to_coordinator.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div className="flex gap-4">
                                <button onClick={() => handleForward(SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_coordinator.value)}
                                    type="button" className="bg-[#212121] hover:bg-[#333333] text-white text-sm font-semibold rounded-lg px-5 py-2.5 transition-colors">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <div>
                    </div>
                </div>
                
                {/* Broadcast Card */}
                <ShowUI
                    ui_key={LEGISLATIVE_DRAFTING_UI_RULES.publish.value}
                    currentStatus={formState.status}
                    forwardStatus={formState.forward_status}
                >
                    <div className="mb-4">
                        <BroadcastCard
                            onClick={() => handleBroadcast(!formState.is_published)}
                            isPublished={formState.is_published || false}
                            title={t("BroadcastTranslationToPublic") || "Broadcast Translation to Public"}
                            description={t("BroadcastTranslationDescription") || "Share your completed legal translation with the public to increase transparency and accessibility. Broadcasting makes the translation visible to all users and stakeholders."}
                            additionalInfo={t("BroadcastTranslationAdditionalInfo") || "Once broadcasted, the translation will be publicly accessible and can be viewed by anyone. You can unpublish it later if needed."}
                        />
                    </div>
                </ShowUI>
                
                {/* Distribute to Zone */}
                <ShowUI
                    ui_key={LEGISLATIVE_DRAFTING_UI_RULES.archived.value}
                    currentStatus={formState.status}
                    forwardStatus={formState.forward_status}
                >
                    <div className="flex mb-4">
                        <button
                            type="button"
                            className="p-4 flex-1 bg-[#212121] text-white rounded-lg"
                            onClick={() => setIsDistributeModalOpen(true)}
                        >{t("ForwardToZone") || "Forward To Zone"}</button>
                    </div>
                    {isDistributeModalOpen &&
                        <DistributeToZone
                            setFormState={setFormState}
                            formState={formState}
                            setEdit={setIsDistributeModalOpen}
                            type="translation"
                        />
                    }
                </ShowUI>
                
                {/* Summary Written Section — shown when a summary exists (the coordinator can still open it to write/edit) */}
                {((contextData?.user?.user_type !== "external") ||
                     (formState.status === "archived" && formState.forward_status === "forwarded_to_director")) &&
                    (formState?.translated_summary ||
                        (contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") &&
                            !(formState.status === "archived" && formState.forward_status === "forwarded_to_director"))) && (
                    <div className="flex-1 bg-white mb-4 p-5 rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="-mx-5 -mt-5 mb-4 bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-5 py-3 flex justify-between items-center">
                            <div>
                                <p className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">{t("OAG") || "OAG"}</p>
                                <p className="text-white font-semibold text-base">{t("SummaryWritten") || "Summary Written"}</p>
                            </div>
                            {contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") &&
                            !(formState.status === "archived" && formState.forward_status === "forwarded_to_director") && (
                                <button
                                    onClick={() => setIsUploadModalOpen(true)}
                                    className="text-sm text-white/90 hover:text-white flex items-center gap-1"
                                >
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="#ffffff" />
                                    </svg>
                                    {t("Edit") || "Edit"}
                                </button>
                            )}
                        </div>
                        <p className="text-[#747171] font-normal text-xs mb-3">{formatDay(formState?.created_at).rest}</p>
                        <p className="font-regular text-base text-[#606060]">{formState?.translated_summary}</p>
                        
                        {/* Latest Draft Translation File */}
                        {(() => {
                            const draftTranslationDocuments = formState?.documents.filter((document: any) => document.document_type === "draft_translation_file");
                            if (draftTranslationDocuments.length === 0) return null;

                            const sortedDocuments = [...draftTranslationDocuments].sort((a, b) => {
                                const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
                                const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
                                return dateB - dateA;
                            });
                            const latestDocument = sortedDocuments[0];
                            const previousDocuments = sortedDocuments.slice(1);

                            return (
                                <>
                                    <div className="mt-3 p-3 rounded-lg border-2 border-green-500 bg-green-50/40">
                                        <p className="text-xs font-semibold text-green-700 mb-2">{t("LatestDraftTranslationFile") || "Latest Draft Translation File"}</p>
                                        <DocumentViewerModal
                                            documents={[latestDocument]}
                                            baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_translation/legal-translation-documents/document`}
                                        />
                                    </div>

                                    {/* Translation Completion Letter */}
                                    {formState?.documents.find((document: any) => document.document_type === "traslation_completion_letter_file") && (
                                        <div className="mt-4">
                                            <p className="text-sm font-medium text-gray-700 mb-2">{t("TranslationCompletionLetter") || "Translation Completion Letter"}</p>
                                            <DocumentViewerModal
                                                documents={formState?.documents.filter((document: any) => document.document_type === "traslation_completion_letter_file")}
                                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_translation/legal-translation-documents/document`}
                                            />
                                        </div>
                                    )}

                                    {/* Previous Draft Translation Files Accordion (Only for internal users) */}
                                    {contextData?.user?.user_type !== "external" && previousDocuments.length > 0 && (
                                        <div className="mt-4">
                                            <button
                                                onClick={() => setShowDraftHistory(!showDraftHistory)}
                                                className="flex items-center justify-between w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                            >
                                                <span className="text-sm font-medium text-gray-700">
                                                    {t("PreviousDrafts", { count: previousDocuments.length }) || `Previous Drafts (${previousDocuments.length})`}
                                                </span>
                                                <svg
                                                    className={`h-5 w-5 text-gray-500 transition-transform ${showDraftHistory ? 'rotate-180' : ''}`}
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            </button>
                                            {showDraftHistory && (
                                                <div className="mt-2 space-y-2 p-3 bg-gray-50 rounded-lg">
                                                    {previousDocuments.map((document) => (
                                                        <div key={document.id} className="p-2 bg-white rounded border">
                                                            <p className="text-xs text-gray-500 mb-1">{formatDay(document.created_at).rest}</p>
                                                            <DocumentViewerModal
                                                                documents={[document]}
                                                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_translation/legal-translation-documents/document`}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                )}

                {/* Request for Additional Info / Summary Written Comments — below the Summary Written card */}
                <ShowUI
                    ui_key={LEGISLATIVE_DRAFTING_UI_RULES.draft_and_additional_file.value}
                    currentStatus={"all"}
                    forwardStatus={formState.forward_status}
                >
                    <ProsecutorRemark setFormState={setFormState} formState={formState} isInternal={isInternal} />
                </ShowUI>

                {/* Upload Draft Law Modal */}
                <UploadDraftLaw
                    request={formState}
                    setLegalResearch={setFormState}
                    isOpen={isUploadModalOpen}
                    onClose={() => setIsUploadModalOpen(false)}
                    onUploadProposal={() => {}}
                    type="legal_translation"
                />
                
                {/* Final Approval Section */}
                <ShowUI
                    ui_key={LEGISLATIVE_DRAFTING_UI_RULES.final_approval.value}
                    currentStatus={formState.status}
                    forwardStatus={formState.forward_status}
                >
                    <LegislativeRespondBackForm
                        request={formState}
                        setLegalResearch={setFormState}
                        isOpen={isRespondBackFormOpen}
                        onClose={() => setIsRespondBackFormOpen(false)}
                        forward={SECTOR_DIRECTIVES_REQUEST_STATUS.audit_responded.value}
                        type="translation"
                    />
                    <div className="flex gap-4">
                        <button onClick={handleRespondBackClick} className="p-4 flex-1 bg-[#D4D4D4] text-[#212121] rounded-lg">{t("RespondBack") || "Respond Back"}</button>
                        <button onClick={handleFinalApprove} className="p-4 flex-1 bg-[#212121] text-white rounded-lg">{t("FinalApproveDraftLaw") || "Final Approve Draft Law"}</button>
                    </div>
                </ShowUI>
                
                {/* Forward Draft Sections */}
                <div className="flex-1 overflow-y-scroll scrollbar-hide">
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.forward_darft.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <LegislativeRespondBackForm
                            request={formState}
                            setLegalResearch={setFormState}
                            isOpen={isRespondBackFormOpen}
                            onClose={() => setIsRespondBackFormOpen(false)}
                            forward={SECTOR_DIRECTIVES_REQUEST_STATUS.draft_responded.value}
                            type="translation"
                        />
                        <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
                            <button
                                type="button"
                                className="inline-flex flex-1 items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors disabled:opacity-50"
                                onClick={handleRespondBackClick}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>{LoadingIcon} {t("Submitting") || "Submitting..."}</>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a5 5 0 015 5v2m-15-7l4-4m-4 4l4 4" />
                                        </svg>
                                        {t("RespondBack") || "Respond back"}
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => handleForward(SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_director.value)}
                                disabled={loading}
                                className="inline-flex flex-1 items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#0A1D39] text-white text-sm font-semibold shadow-sm hover:bg-[#13294d] focus:outline-none focus:ring-2 focus:ring-[#0A1D39]/40 transition-colors disabled:opacity-50"
                            >
                                {loading ? (
                                    <>{LoadingIcon} {t("Submitting") || "Submitting..."}</>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                        {t("ApproveAndForwardToDirector") || "Approve and Forward to Director"}
                                    </>
                                )}
                            </button>
                        </div>
                    </ShowUI>
                    
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.forward_darft_to_director.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <LegislativeRespondBackForm
                            request={formState}
                            setLegalResearch={setFormState}
                            isOpen={isRespondBackFormOpen}
                            onClose={() => setIsRespondBackFormOpen(false)}
                            forward={SECTOR_DIRECTIVES_REQUEST_STATUS.draft_responded.value}
                            type="translation"
                        />
                        <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
                            <button
                                type="button"
                                className="inline-flex flex-1 items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors disabled:opacity-50"
                                onClick={handleRespondBackClick}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>{LoadingIcon} {t("Submitting") || "Submitting..."}</>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a5 5 0 015 5v2m-15-7l4-4m-4 4l4 4" />
                                        </svg>
                                        {t("RespondBack") || "Respond back"}
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (isDirector) {
                                        handleForward(SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_head.value);
                                    } else {
                                        handleForward(SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_director.value);
                                    }
                                }}
                                disabled={loading}
                                className="inline-flex flex-1 items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#0A1D39] text-white text-sm font-semibold shadow-sm hover:bg-[#13294d] focus:outline-none focus:ring-2 focus:ring-[#0A1D39]/40 transition-colors disabled:opacity-50"
                            >
                                {loading ? (
                                    <>{LoadingIcon} {t("Submitting") || "Submitting..."}</>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                        {isDirector
                                            ? (t("ApproveAndForwardToHead") || "Approve and Forward to Head")
                                            : (t("ApproveAndForwardToDirector") || "Approve and Forward to Director")}
                                    </>
                                )}
                            </button>
                        </div>
                    </ShowUI>
                    
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.forward_darft_to_head.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <LegislativeRespondBackForm
                            request={formState}
                            setLegalResearch={setFormState}
                            isOpen={isRespondBackFormOpen}
                            onClose={() => setIsRespondBackFormOpen(false)}
                            forward={SECTOR_DIRECTIVES_REQUEST_STATUS.draft_responded.value}
                            type="translation"
                        />
                        <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
                            <button
                                type="button"
                                className="inline-flex flex-1 items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors disabled:opacity-50"
                                onClick={handleRespondBackClick}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>{LoadingIcon} {t("Submitting") || "Submitting..."}</>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a5 5 0 015 5v2m-15-7l4-4m-4 4l4 4" />
                                        </svg>
                                        {t("RespondBack") || "Respond back"}
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={handleDraftApprove}
                                disabled={loading}
                                className="inline-flex flex-1 items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#0A1D39] text-white text-sm font-semibold shadow-sm hover:bg-[#13294d] focus:outline-none focus:ring-2 focus:ring-[#0A1D39]/40 transition-colors disabled:opacity-50"
                            >
                                {loading ? (
                                    <>{LoadingIcon} {t("Submitting") || "Submitting..."}</>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                        {t("Approve") || "Approve"}
                                    </>
                                )}
                            </button>
                        </div>
                    </ShowUI>
                    
                    {/* Documents Section */}
                    <div className="mt-6 space-y-4 bg-white border border-gray-200 shadow-sm p-5 mb-8 rounded-xl">
                        {formState?.documents.find((document: any) => document.document_type === "translation_request_document") &&
                            <>
                                {formState?.registry_number &&
                                    <p className="text-[#432101AB] font-normal text-base">{t("RegisterNumber") || "Register Number"}: {formState.registry_number}</p>
                                }
                                <p className="text-[#432101AB] font-normal text-base">{t("RegisterLetterNumber") || "Register Letter Number"}: {formState?.request_letter_reference_number}</p>
                                <DocumentViewerModal
                                    documents={formState?.documents.filter((document: any) => document.document_type === "translation_request_document")}
                                    baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_translation/legal-translation-documents/document`}
                                />
                            </>
                        }
                        {formState?.documents.find((document: any) => document.document_type === "traslation_request_letter_file") &&
                            <DocumentViewerModal
                                documents={formState?.documents.filter((document: any) => document.document_type === "traslation_request_letter_file")}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_translation/legal-translation-documents/document`}
                            />
                        }
                        <div className="mb-8">
                            <p className="text-[#432101AB] font-normal text-base">{t("LegalTranslationSummaryNote") || "Legal Translation Summary Note"}</p>
                            <p className="text-gray-500 font-medium mb-2">
                                {formState?.request_summary}
                            </p>
                        </div>
                        
                        {/* Show these fields for external/stakeholder type */}
                        {formState?.requested_by === "Stakeholder" && (
                            <>
                                {formState?.organization_name && (
                                    <div className="mb-2">
                                        <p className="text-[#432101AB] font-normal text-base">{t("Stakeholder") || "Stakeholder"}</p>
                                        <p className="text-gray-500 font-medium mb-2">{formState?.organization_name}</p>
                                    </div>
                                )}
                                {formState?.organization_type && (
                                    <div className="mb-2">
                                        <p className="text-[#432101AB] font-normal text-base">{t("StakeholderType") || "Stakeholder Type"}</p>
                                        <p className="text-gray-500 font-medium mb-2">{formState?.organization_type}</p>
                                    </div>
                                )}
                                {formState?.applied_date && (
                                    <div className="mb-2">
                                        <p className="text-[#432101AB] font-normal text-base">{t("AppliedDate") || "Applied Date"}</p>
                                        <p className="text-gray-500 font-medium mb-2">{formState?.applied_date}</p>
                                    </div>
                                )}
                                {(formState?.organization_state || formState?.organization_zone || formState?.organization_kebel) && (
                                    <div className="mb-2">
                                        <p className="text-[#432101AB] font-normal text-base">{t("Address") || "Address"}</p>
                                        <p className="text-gray-500 font-medium mb-2">
                                            {[formState?.organization_state, formState?.organization_zone, formState?.organization_kebel].filter(Boolean).join(", ")}
                                        </p>
                                    </div>
                                )}
                                {formState?.email_user_organization && (
                                    <div className="mb-2">
                                        <p className="text-[#432101AB] font-normal text-base">{t("Email") || "Email"}</p>
                                        <p className="text-gray-500 font-medium mb-2">{formState?.email_user_organization}</p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    
                    {/* Remark Section for internal requests */}
                    {isInternal && (
                        <>
                            <ShowUI
                                ui_key={LEGISLATIVE_DRAFTING_UI_RULES.internal_ui.value}
                                currentStatus={"all"}
                                forwardStatus={"all"}
                            >
                                {formState.remarks && formState?.remarks?.find((remark: any) => remark.role === "head") &&
                                    <div>
                                        <p className="text-[#432101AB] font-normal text-base">{t("RemarkForDirector") || "Remark For Director"}</p>
                                        <p className="text-gray-500 font-medium mb-2">
                                            {formState?.remarks.filter((remark: any) => remark.role === "head").slice().reverse()[0]?.remark}
                                        </p>
                                        <br />
                                    </div>
                                }
                                {formState.remarks && formState?.remarks?.find((remark: any) => remark.role === "director") &&
                                    <div>
                                        <p className="text-[#432101AB] font-normal text-base">{t("RemarkForCoordinator") || "Remark For Coordinator"}</p>
                                        <p className="text-gray-500 font-medium mb-2">
                                            {formState?.remarks.filter((remark: any) => remark.role === "director").slice().reverse()[0]?.remark}
                                        </p>
                                        <br />
                                    </div>
                                }
                                {formState.remarks && formState?.remarks?.find((remark: any) => remark.role === "coordinator") &&
                                    <div>
                                        <p className="text-[#432101AB] font-normal text-base">{t("RemarkForProsecutor") || "Remark For Prosecutor"}</p>
                                        <p className="text-gray-500 font-medium mb-2">
                                            {formState?.remarks.filter((remark: any) => remark.role === "coordinator").slice().reverse()[0]?.remark}
                                        </p>
                                        <br />
                                    </div>
                                }
                            </ShowUI>
                        </>
                    )}

                    {/* Prosecutor/Team assignment — shown for internal and external */}
                    {formState?.prosecutor_assignments && formState?.prosecutor_assignments.length > 0 && (
                                <div className="mt-6 mb-6 flex flex-col gap-2 bg-gray-50 rounded-lg border border-gray-200 p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="font-semibold text-gray-700">{t("ProsecutorAssignmentsTeam") || "Prosecutor Assignments / Team"}</span>
                                        {(isDirector || contextData?.user?.roles?.includes("LegalResearchCoordinator")) && (
                                            <button
                                                className="ml-2 bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-sm"
                                                onClick={() => setEdit(true)}
                                            >{t("EditTeam") || "Edit Team"}</button>
                                        )}
                                        <button
                                            className="ml-2 bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-sm"
                                            onClick={() => setViewTeam(true)}
                                        >{t("ViewTeam") || "View Team"}</button>
                                    </div>
                                    <ActionAvatars
                                        title={t("TeamList") || "Team List"}
                                        users={formState?.prosecutor_assignments}
                                        prosecutor={true}
                                    />
                                </div>
                            )}

                    {/* Edit Team Modal */}
                    {edit && formState.status !== "archived" && (
                        <EditTeamModal
                            request={formState}
                            isOpen={edit}
                            onClose={() => setEdit(false)}
                            onSave={() => {}}
                        />
                    )}
                    <br />
                    
                    {/* Additional Information Section */}
                    {!!formState.additional_infos?.[0] &&
                        <div className="flex-1 bg-white mb-4 p-5 rounded-xl border border-gray-200 shadow-sm">
                            <div className="pb-2 border-b border-gray-200 mb-4">
                                <p>{t("AdditionalInformationRequest") || "Additional Information Request"}</p>
                            </div>
                            <p className="text-[#747171] font-normal text-xs mb-3">{formatDay(formState?.additional_infos?.[0].requested_at).rest}</p>
                            <p className="font-regular text-base text-[#606060]">{formState?.additional_infos?.[0].request_summary}</p>
                            {formState?.additional_infos[0].documents.find((document: any) => document.document_type === "translaton_additional_file") &&
                                <DocumentViewerModal
                                    documents={formState?.additional_infos[0].documents.filter((document: any) => document.document_type === "translaton_additional_file")}
                                    baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_translation/legal-translation-additional-info-documents/document`}
                                />
                            }
                            {!!formState.additional_infos?.[0] && !(formState.status === "Waiting for Additional Information") &&
                                <>
                                    <div className="pb-2 border-b border-gray-200 mb-4">
                                        <p>{t("AdditionalInformationResponse") || "Additional Information Response"}</p>
                                    </div>
                                    <p className="text-[#747171] font-normal text-xs mb-3">{formatDay(formState?.additional_infos[0].responded_at).rest}</p>
                                    <p className="font-regular w-[55ch] text-justify text-base text-[#606060]">{formState?.additional_infos[0].response_note}</p>
                                    {formState?.additional_infos[0].documents.find((document: any) => document.document_type === "translation_additional_response_file") &&
                                        <DocumentViewerModal
                                            documents={formState?.additional_infos[0].documents.filter((document: any) => document.document_type === "translation_additional_response_file")}
                                            baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_translation/legal-translation-additional-info-documents/document`}
                                        />
                                    }
                                </>
                            }
                        </div>
                    }

                    {/* Remark Section */}
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.internal_ui.value}
                        currentStatus={"all"}
                        forwardStatus={"all"}
                    >
                        {formState.remarks && formState?.remarks?.find((remark: any) => remark.role === "head") &&
                            <div>
                                <p className="text-[#432101AB] font-normal text-base">{t("RemarkForDirector") || "Remark For Director"}</p>
                                <p className="text-gray-500 font-medium mb-2">
                                    {formState?.remarks.filter((remark: any) => remark.role === "head").slice().reverse()[0]?.remark}
                                </p>
                                <br />
                            </div>
                        }
                        {formState.remarks && formState?.remarks?.find((remark: any) => remark.role === "director") &&
                            <div>
                                <p className="text-[#432101AB] font-normal text-base">{t("RemarkForCoordinator") || "Remark For Coordinator"}</p>
                                <p className="text-gray-500 font-medium mb-2">
                                    {formState?.remarks.filter((remark: any) => remark.role === "director").slice().reverse()[0]?.remark}
                                </p>
                                <br />
                            </div>
                        }
                        {formState.remarks && formState?.remarks?.find((remark: any) => remark.role === "coordinator") &&
                            <div>
                                <p className="text-[#432101AB] font-normal text-base">{t("RemarkForProsecutor") || "Remark For Prosecutor"}</p>
                                <p className="text-gray-500 font-medium mb-2">
                                    {formState?.remarks.filter((remark: any) => remark.role === "coordinator").slice().reverse()[0]?.remark}
                                </p>
                                <br />
                            </div>
                        }
                    </ShowUI>
                    
                    {/* HeadRemark, DirectorRemark, CoordinatorRemark, ProsecutorRemark */}
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_head_forward_to_director.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <HeadRemark type="legal_translation" setEdit={setEdit} setFormState={setFormState} formState={formState} />
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_director_forward_to_coordinator.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <DirectorRemark type="legal_translation" setEdit={setEdit} setFormState={setFormState} formState={formState} />
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_coordinator_forward_to_prosecutor.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <CoordinatorRemark type="legal_translation" setEdit={setEdit} setFormState={setFormState} formState={formState} />
                    </ShowUI>
                </div>
            </div>
            
            {/* Modals */}
            {publicFeedback &&
                (<PublicFeedback
                    data={formState}
                    setLegalResearch={setFormState}
                    isOpen={publicFeedback}
                    onClose={() => setPublicFeedback(false)}
                />)
            }
            {uploadAmend &&
                (<UploadAmendedFile
                    data={formState}
                    setLegalResearch={setFormState}
                    isOpen={uploadAmend}
                    onClose={() => setUploadAmend(false)}
                />)
            }
            {viewTeam &&
                <TeamView teamMembers={formState.prosecutor_assignments} onClose={() => setViewTeam(false)} />}
        </div>
    );
}

export default LegalAdvisoryDetail;